import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import api from '../../../utils/api'
import { toast } from 'react-toastify';
import AdminLayout from '../components/AdminLayout';
import { useSortableData, SortDirection } from '../components/SortableTable';
import { useUserTimezone } from '../../../hooks/useUserTimezone';

interface Publication {
  id: number;
  name: string;
  active: boolean;
  internal: boolean;
  billingType: string;
  createdAt: string;
  accountName: string;
  avatarUrl?: string;
}

interface AutocompleteResult {
  id: number;
  text: string;
}

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #d97706;
  margin: 0;
`;

const CreateButton = styled(Link)`
  padding: 10px 20px;
  background: #6b7280;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  text-decoration: none;
  &:hover { background: #4b5563; color: white; }
`;

const FilterSection = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
  position: relative;
`;

const AutocompleteWrapper = styled.div`
  position: relative;
  flex: 1;
  max-width: 400px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
  background: white;
  box-sizing: border-box;
  &:focus { outline: none; border-color: #00a6c2; }
  &::placeholder { color: #9ca3af; }
`;

const AutocompleteDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #d1d5db;
  border-top: none;
  border-radius: 0 0 4px 4px;
  max-height: 300px;
  overflow-y: auto;
  z-index: 100;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const AutocompleteItem = styled.div<{ $highlighted?: boolean }>`
  padding: 10px 14px;
  cursor: pointer;
  font-size: 14px;
  background: ${({ $highlighted }) => ($highlighted ? '#f3f4f6' : 'white')};
  &:hover { background: #f3f4f6; }
`;

const GoButton = styled.button`
  padding: 10px 24px;
  background: #00a6c2;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  &:hover { background: #0094ab; }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border: 1px solid #e5e7eb;
`;

const Th = styled.th<{ $sortable?: boolean; $sorted?: boolean }>`
  text-align: left;
  padding: 12px;
  font-size: 13px;
  font-weight: 600;
  color: ${({ $sorted }) => ($sorted ? '#00a6c2' : '#374151')};
  border-bottom: 2px solid #e5e7eb;
  background: #f9fafb;
  cursor: ${({ $sortable }) => ($sortable ? 'pointer' : 'default')};
  user-select: none;
  white-space: nowrap;
  
  &:hover {
    ${({ $sortable }) => $sortable && `
      background: #f0f0f0;
      color: #00a6c2;
    `}
  }
`;

const SortIndicator = styled.span<{ $direction: SortDirection }>`
  margin-left: 4px;
  
  &::after {
    content: '${({ $direction }) => {
      if ($direction === 'asc') return '↓';
      if ($direction === 'desc') return '↑';
      return '';
    }}';
  }
`;

const Td = styled.td`
  padding: 12px;
  font-size: 14px;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
  vertical-align: middle;
`;

const PubCell = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Avatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 4px;
  object-fit: cover;
`;

const PlaceholderAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 4px;
  background: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 12px;
`;

const PubInfo = styled.div`
  a { color: #d97706; text-decoration: none; font-weight: 500; &:hover { text-decoration: underline; } }
  small { display: block; color: #6b7280; font-size: 12px; }
`;

const BillingLink = styled(Link)`
  color: #00a6c2;
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 4px;
  margin-top: 24px;
`;

const PageButton = styled.button<{ $active?: boolean }>`
  padding: 6px 12px;
  border: 1px solid ${({ $active }) => ($active ? '#00a6c2' : '#d1d5db')};
  background: ${({ $active }) => ($active ? '#00a6c2' : 'white')};
  color: ${({ $active }) => ($active ? 'white' : '#374151')};
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  &:hover { background: ${({ $active }) => ($active ? '#0094ab' : '#f3f4f6')}; }
  &:disabled { cursor: not-allowed; opacity: 0.5; }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 48px;
  color: #6b7280;
`;

const ITEMS_PER_PAGE = 20;

function AdminPublicationsPage() {
  const navigate = useNavigate();
  const [publications, setPublications] = useState<Publication[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const { setTimezoneFromResponse, formatDateTime } = useUserTimezone();
  
  // Sorting
  const { sortedData, requestSort, getSortDirection } = useSortableData(publications, { key: 'createdAt', direction: 'desc' });
  
  // Autocomplete state
  const [autocompleteResults, setAutocompleteResults] = useState<AutocompleteResult[]>([]);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [selectedPubId, setSelectedPubId] = useState<number | null>(null);
  const autocompleteRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const fetchPublications = useCallback(async (pageNum: number, pubId?: number | null) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pageNum.toString());
      params.append('per_page', ITEMS_PER_PAGE.toString());
      if (pubId) params.append('id', pubId.toString());

      const response = await api.get(`/admin2/publications.json?${params.toString()}`);
      const data = response.data;
      
      setPublications(data.publications || []);
      setTotalPages(data.totalPages || Math.ceil((data.total || 0) / ITEMS_PER_PAGE) || 1);
      setTimezoneFromResponse(data);
    } catch (error) {
      toast.error('Failed to load publications.');
      setPublications([]);
    } finally {
      setLoading(false);
    }
  }, [setTimezoneFromResponse]);

  const fetchAutocomplete = useCallback(async (query: string) => {
    if (!query || query.length < 2) {
      setAutocompleteResults([]);
      setShowAutocomplete(false);
      return;
    }
    
    try {
      const response = await api.get(`/admin2/publications/autocomplete.json?query=${encodeURIComponent(query)}`);
      setAutocompleteResults(response.data || []);
      setShowAutocomplete(true);
      setHighlightedIndex(-1);
    } catch (error) {
      setAutocompleteResults([]);
    }
  }, []);

  useEffect(() => {
    fetchPublications(page);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (autocompleteRef.current && !autocompleteRef.current.contains(event.target as Node)) {
        setShowAutocomplete(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    setSelectedPubId(null);
    fetchAutocomplete(value);
  };

  const handleSelectAutocomplete = (result: AutocompleteResult) => {
    setSearchQuery(result.text);
    setSelectedPubId(result.id);
    setShowAutocomplete(false);
    // Navigate directly to the publication edit page
    navigate(`/admin2/publications/${result.id}/edit`);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showAutocomplete || autocompleteResults.length === 0) return;
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => Math.min(prev + 1, autocompleteResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && highlightedIndex >= 0) {
      e.preventDefault();
      handleSelectAutocomplete(autocompleteResults[highlightedIndex]);
    } else if (e.key === 'Escape') {
      setShowAutocomplete(false);
    }
  };

  const handleSearch = () => {
    if (selectedPubId) {
      navigate(`/admin2/publications/${selectedPubId}/edit`);
    } else {
      setPage(1);
      fetchPublications(1);
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    fetchPublications(newPage, selectedPubId);
  };

  const renderPagination = () => {
    const pages: (number | string)[] = [];
    pages.push(1);
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) {
      if (!pages.includes(i)) pages.push(i);
    }
    if (totalPages > 1 && !pages.includes(totalPages)) {
      pages.push(totalPages);
    }
    const result: (number | string)[] = [];
    let prev = 0;
    for (const p of pages.sort((a, b) => (a as number) - (b as number))) {
      if ((p as number) - prev > 1) result.push('...');
      result.push(p);
      prev = p as number;
    }
    return result;
  };

  if (loading && publications.length === 0) {
    return (
      <AdminLayout title="Publications" pageTitle="Publications">
        <LoadingState>Loading...</LoadingState>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Publications" pageTitle="Publications">
      <PageHeader>
        <PageTitle>Publications</PageTitle>
        <CreateButton to="/admin2/publications/new">Create a publication</CreateButton>
      </PageHeader>

      <FilterSection>
        <AutocompleteWrapper ref={autocompleteRef}>
          <SearchInput
            ref={inputRef}
            type="text"
            placeholder="Publication"
            value={searchQuery}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => searchQuery.length >= 2 && setShowAutocomplete(true)}
          />
          {showAutocomplete && autocompleteResults.length > 0 && (
            <AutocompleteDropdown>
              {autocompleteResults.map((result, idx) => (
                <AutocompleteItem
                  key={result.id}
                  $highlighted={idx === highlightedIndex}
                  onClick={() => handleSelectAutocomplete(result)}
                >
                  {result.text}
                </AutocompleteItem>
              ))}
            </AutocompleteDropdown>
          )}
        </AutocompleteWrapper>
        <GoButton onClick={handleSearch}>Go</GoButton>
      </FilterSection>

      <Table>
        <thead>
          <tr>
            <Th $sortable $sorted={getSortDirection('name') !== null} onClick={() => requestSort('name')}>
              Name <SortIndicator $direction={getSortDirection('name')} />
            </Th>
            <Th $sortable $sorted={getSortDirection('active') !== null} onClick={() => requestSort('active')}>
              Active <SortIndicator $direction={getSortDirection('active')} />
            </Th>
            <Th $sortable $sorted={getSortDirection('internal') !== null} onClick={() => requestSort('internal')}>
              Internal <SortIndicator $direction={getSortDirection('internal')} />
            </Th>
            <Th $sortable $sorted={getSortDirection('billingType') !== null} onClick={() => requestSort('billingType')}>
              Billing Type <SortIndicator $direction={getSortDirection('billingType')} />
            </Th>
            <Th $sortable $sorted={getSortDirection('createdAt') !== null} onClick={() => requestSort('createdAt')}>
              Created at <SortIndicator $direction={getSortDirection('createdAt')} />
            </Th>
          </tr>
        </thead>
        <tbody>
          {sortedData.map((pub) => (
            <tr key={pub.id}>
              <Td>
                <PubCell>
                  {pub.avatarUrl ? (
                    <Avatar src={pub.avatarUrl} alt={pub.name} />
                  ) : (
                    <PlaceholderAvatar>📄</PlaceholderAvatar>
                  )}
                  <PubInfo>
                    <Link to={`/admin2/publications/${pub.id}/edit`}>{pub.name}</Link>
                    <small>{pub.accountName}</small>
                  </PubInfo>
                </PubCell>
              </Td>
              <Td>{pub.active ? 'true' : 'false'}</Td>
              <Td>{pub.internal ? 'true' : 'false'}</Td>
              <Td>
                <BillingLink to={`/admin2/publications/${pub.id}/edit`}>{pub.billingType}</BillingLink>
              </Td>
              <Td>{formatDateTime(pub.createdAt)}</Td>
            </tr>
          ))}
        </tbody>
      </Table>

      {totalPages > 1 && (
        <Pagination>
          {renderPagination().map((item, idx) => (
            typeof item === 'number' ? (
              <PageButton
                key={idx}
                $active={item === page}
                onClick={() => handlePageChange(item)}
              >
                {item}
              </PageButton>
            ) : (
              <span key={idx} style={{ padding: '0 8px', color: '#6b7280' }}>...</span>
            )
          ))}
          <PageButton
            onClick={() => handlePageChange(page + 1)}
            disabled={page >= totalPages}
          >
            →
          </PageButton>
          <PageButton
            onClick={() => handlePageChange(totalPages)}
            disabled={page >= totalPages}
          >
            ⇥
          </PageButton>
        </Pagination>
      )}
    </AdminLayout>
  );
}

export default AdminPublicationsPage;
